import type { ICategory } from "app/common/services/categoryService.types";
import type { ICountryObject } from "app/services/CountryService";
import { allTrackers } from "app/services/track/track";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";
import { SwRedux } from "app/services/SwRedux";

import type { CreatedIndustry } from "../CustomHooks/customIndustryAgent.types";
import { navigateToCategory } from "./navigateToIndustry";
import { customIndustryAgentRunService } from "./customIndustryAgentRun/customIndustryAgentRunService";

const SEGMENT_SHARING_POLL_INTERVAL_MS = 1000;
// ~4s of slack for the backend to finish marking the run's new segments as shared with the
// org — generous enough to absorb the usual propagation delay without stalling the redirect
// for long on the rare run that never converges.
const SEGMENT_SHARING_POLL_MAX_ATTEMPTS = 5;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Newly created segments can take a moment to propagate as "shared with the organization" on
 *  the backend. `SegmentsApiService.refreshSegmentsMetaData` below is a one-shot refetch, so it
 *  can race that propagation and cache a still-unshared snapshot into Redux — and nothing else
 *  re-fetches it for the rest of this SPA session (`loadCustomSegmentsMetadata`'s "already
 *  cached" guard skips once anything is populated), which is what strands the user on the
 *  Overview page's "No access" state until they hard-reload (a fresh page load starts with an
 *  empty cache and re-fetches, by which time the backend has usually caught up). Polling a few
 *  times before navigating resolves the common case before the redirect ever fires. */
const waitForSegmentsSharedWithOrg = async (createdCategory: ICategory): Promise<void> => {
    for (let attempt = 0; attempt < SEGMENT_SHARING_POLL_MAX_ATTEMPTS; attempt++) {
        // Refetching happens between checks, never after the last one — the caller navigates
        // either way, so a final round-trip nobody reads is wasted.
        if (attempt > 0) {
            await wait(SEGMENT_SHARING_POLL_INTERVAL_MS);
            await SegmentsApiService.getInstance().refreshSegmentsMetaData();
        }

        // `SwRedux.getState()` is untyped (`any`) at its own declaration.
        const { segmentsModule } = SwRedux.getInstance().getState() as { segmentsModule: ISegmentsModule };
        if (!CategoryServiceUtils.hasUnauthorizedSegment(createdCategory, segmentsModule)) {
            return;
        }
    }
};

/** What the navigated-to industry turned out to be, so a caller that has to follow the
 *  redirect (syncing a country filter, say) does not repeat the lookup this already did. */
export type OpenedIndustry = {
    country: ICountryObject | undefined;
};

/** Navigates to the industry the agent just created, or resolves `null` when it is not in
 *  the user's own categories even after the refresh above — a failed hand-off, not a wait. */
export const openCreatedIndustry = async (
    { industryId, industryName }: CreatedIndustry,
    fallbackCountryId?: number,
): Promise<OpenedIndustry | null> => {
    await Promise.all([
        UserCustomCategoryService.refreshCustomCategories(),
        SegmentsApiService.getInstance().refreshSegmentsMetaData(),
    ]);

    // By id, not by name: the id the agent reports is the one Pro assigned on creation, while
    // two industries can carry the same name.
    const createdCategory = UserCustomCategoryService.getCustomCategoryById(industryId);
    if (!createdCategory) {
        return null;
    }

    await waitForSegmentsSharedWithOrg(createdCategory);

    allTrackers.dispatchTrackEvent("Button", "click", "marketBuilder/aiIndustryCreated", {
        industryId,
        industryName,
    });

    const country = CategoryServiceUtils.getIndustryCountry(createdCategory);

    await navigateToCategory(createdCategory, country?.id ?? fallbackCountryId, { editIndustryId: industryId });
    // Only once the route changed: a navigation that fails leaves the run to report it on.
    customIndustryAgentRunService.dismiss();

    return { country };
};
